import get from "lodash/get"
import find from "lodash/find"

import { appicalPartnerHash } from "constants/internal"
import { CREATE, UPDATE, DELETE, SELECT } from "redux/utility"

import { selectLoggedInUser } from "redux/reducers/entities/users"

import {
    SET_COMPANIES,
    COMPANY,
    TRANSFER_OWNERSHIP,
    BILLING_INFO,
    PAYMENT_INFO,
    SEAT_COUNT,
} from "./actionNames"
import {
    selectCompanies,
    selectSelectedHash as selectCurrentCompanyHash,
    selectData,
} from "."

export const createSetCompanies = companies => ({
    type: SET_COMPANIES,
    payload: companies,
})

export const createSelectCompany = companyHash => (dispatch, getState) => {
    const state = getState()
    const companies = selectData(state)
    const loggedInUser = selectLoggedInUser(state)

    dispatch({
        type: COMPANY + SELECT,
        payload: {
            selectedHash: companyHash,
            company: get(companies, companyHash, {}),
            loggedInUser,
        },
    })
}

export const createLoadBillingInfoAction =
    (companyHash = null, callback) =>
    (dispatch, getState) => {
        const currentCompanyHash =
            companyHash || selectCurrentCompanyHash(getState())

        dispatch({
            type: COMPANY + BILLING_INFO,
            request: {
                method: "GET",
                url: `/editor/Payment/Subscription/${currentCompanyHash}`,
            },
            payload: { companyHash: currentCompanyHash },
            callback,
        })
    }

export const createLoadSubscriptionSeatsAction =
    (companyHash = null, callback) =>
    (dispatch, getState) => {
        const currentCompanyHash =
            companyHash || selectCurrentCompanyHash(getState())

        dispatch({
            type: COMPANY + SEAT_COUNT,
            request: {
                method: "GET",
                url: `/editor/Payment/CompanySeatPlan/${currentCompanyHash}`,
            },
            payload: { companyHash: currentCompanyHash },
            callback,
        })
    }

export const createCreateAction =
    (data, callback = null) =>
    dispatch => {
        const {
            companyName,
            environmentName,
            billingInfo,
            agreesToSLA,
            agreesToDPA,
        } = data
        // DPA is optional
        const agreesToAllPolicies = agreesToSLA || (agreesToSLA && agreesToDPA)

        dispatch({
            type: COMPANY + CREATE,
            request: {
                method: "POST",
                url: "/editor/Company/CreateCompany",
                data: {
                    parentHash: appicalPartnerHash,
                    agreesToAllPolicies,
                    companyName,
                    environmentName,
                },
            },
            payload: {
                parentHash: appicalPartnerHash,
                environment: environmentName,
                name: companyName,
                agreesToAllPolicies,
                billingInfo,
            },
            callback,
        })
    }

export const createUpdateCompany =
    (companyName, languageId, callback = null) =>
    (dispatch, getState) => {
        const state = getState()
        const companyHash = selectCurrentCompanyHash(state)
        const loggedInUser = selectLoggedInUser(state)

        dispatch({
            type: COMPANY + UPDATE,
            request: {
                method: "POST",
                url: "/editor/Company/Update",
                data: {
                    companyHash,
                    companyName,
                    languageId,
                },
            },
            payload: {
                hash: companyHash,
                name: companyName,
                languageId,
                userLanguageId: loggedInUser?.languageId || languageId,
            },
            callback,
        })
    }

export const createUpdateBillingInfoAction =
    (billingInfo, companyHash = null, callback) =>
    (dispatch, getState) => {
        const currentCompanyHash =
            companyHash || selectCurrentCompanyHash(getState())

        dispatch({
            type: COMPANY + BILLING_INFO + UPDATE,
            request: {
                method: "POST",
                url: "/editor/Payment/UpdateBillingInfo",
                data: { companyHash: currentCompanyHash, billingInfo },
            },
            payload: { companyHash: currentCompanyHash, billingInfo },
            callback,
        })
    }

export const createUpdatePaymentInfoAction =
    (paymentToken, cardInfo, companyHash = null, callback) =>
    (dispatch, getState) => {
        const currentCompanyHash =
            companyHash || selectCurrentCompanyHash(getState())

        dispatch({
            type: COMPANY + PAYMENT_INFO + UPDATE,
            request: {
                method: "POST",
                url: "/editor/Payment/UpdatePaymentInfo",
                data: { companyHash: currentCompanyHash, paymentToken },
            },
            payload: { companyHash: currentCompanyHash, cardInfo },
            callback,
        })
    }

export const createTransferOwnership =
    (newOwnerUserHash, currentPassword, callback = null) =>
    (dispatch, getState) =>
        dispatch({
            type: COMPANY + TRANSFER_OWNERSHIP,
            request: {
                method: "POST",
                url: "/editor/Company/Update",
                data: {
                    companyHash: selectCurrentCompanyHash(getState()),
                    newOwnerUserHash,
                    currentPassword,
                },
            },
            payload: {
                hash: selectCurrentCompanyHash(getState()),
                newOwnerUserHash,
            },
            callback,
        })

export const createDeleteCompany =
    (callback = null) =>
    (dispatch, getState) =>
        dispatch({
            type: COMPANY + DELETE,
            request: {
                method: "POST",
                url: "/editor/Company/Delete",
                data: {
                    companyHash: selectCurrentCompanyHash(getState()),
                },
            },
            payload: {
                hash: selectCurrentCompanyHash(getState()),
            },
            callback,
        })

export const createSelectCompanyByEnvironmentAction =
    targetEnvironment => (dispatch, getState) => {
        const companies = selectCompanies(getState())
        const company = find(
            companies,
            ({ environment }) => environment === targetEnvironment,
        )

        dispatch(createSelectCompany(get(company, "hash")))
    }

export const createDeselectCompanyAction = () => createSelectCompany(null)
